import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { AuthSessionService } from '@app/core/auth/services/auth-session.service';

export interface DashboardTypeItem {
  readonly u_name: string;
  readonly sys_id: string;
  readonly u_workspace: string;
  readonly u_chart: string;
  readonly u_section: string;
  readonly u_env: string;
  readonly u_key: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardTypesService {
  private readonly authSession = inject(AuthSessionService);

  readonly dashboards = computed<readonly DashboardTypeItem[]>(() =>
    this.resolveDashboardsFromPersona(this.authSession.personaDetails()?.dashboards),
  );
  readonly selectedDashboardId = signal<string | null>(null);
  readonly filteredDashboards = computed(() => {
    if (!this.authSession.hasPersonaAccess('powerBi')) {
      return [] as readonly DashboardTypeItem[];
    }

    return this.dashboards();
  });

  readonly hasTrendOptions = computed(() => this.filteredDashboards().length > 0);

  readonly trendOptions = computed(() => this.filteredDashboards());

  readonly selectedDashboard = computed(() => {
    const selectedId = this.selectedDashboardId();
    if (selectedId === null) {
      return null;
    }

    return this.filteredDashboards().find((dashboard) => dashboard.sys_id === selectedId) ?? null;
  });

  constructor() {
    effect(() => {
      const availableDashboards = this.filteredDashboards();
      const currentId = this.selectedDashboardId();

      if (!availableDashboards.length) {
        if (currentId !== null) {
          this.selectedDashboardId.set(null);
        }
        return;
      }

      if (currentId && availableDashboards.some((dashboard) => dashboard.sys_id === currentId)) {
        return;
      }

      this.selectedDashboardId.set(availableDashboards[0].sys_id);
    });
  }

  selectDashboardById(dashboardId: string): void {
    const exists = this.filteredDashboards().some((dashboard) => dashboard.sys_id === dashboardId);
    if (!exists) {
      return;
    }

    this.selectedDashboardId.set(dashboardId);
  }

  private resolveDashboardsFromPersona(rawDashboards: unknown): readonly DashboardTypeItem[] {
    if (!Array.isArray(rawDashboards)) {
      return [];
    }

    return rawDashboards
      .map((dashboard) => {
        if (!this.isRecord(dashboard)) {
          return null;
        }

        const dashboardSysId = this.toCleanString(
          dashboard['dashboardSysId'] ?? dashboard['dashboard_sys_id'],
        );
        const dashboardName = this.toCleanString(dashboard['name']);
        const workspace = this.toCleanString(dashboard['workspace']);
        const chart = this.toCleanString(dashboard['chart']);
        const section = this.toCleanString(dashboard['section']);
        const env = this.toCleanString(dashboard['env']);
        const key = this.toCleanString(dashboard['key']);

        if (!dashboardSysId || !workspace || !chart || !section || !env || !key) {
          return null;
        }

        return {
          u_name: dashboardName || dashboardSysId,
          sys_id: dashboardSysId,
          u_workspace: workspace,
          u_chart: chart,
          u_section: section,
          u_env: env,
          u_key: key,
        } as DashboardTypeItem;
      })
      .filter((dashboard): dashboard is DashboardTypeItem => dashboard !== null);
  }

  private toCleanString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
